<template>
  <teleport to="body">
    <div v-if="visible" class="sp-overlay" @mousedown.self="close">
      <div
        class="sp-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Recherche"
      >
        <!-- Input -->
        <div class="sp-input-wrap">
          <svg class="sp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
          <input
            ref="inputEl"
            v-model="query"
            class="sp-input"
            type="text"
            placeholder="Rechercher des cartes, dossiers…"
            autocomplete="off"
            @keydown="handleKeydown"
          />
          <kbd class="sp-esc-hint">Esc</kbd>
        </div>

        <!-- Results -->
        <div v-if="hasResults" class="sp-results" ref="resultsEl">

          <!-- CARTES -->
          <div v-if="cardResults.length > 0" class="sp-group">
            <div class="sp-group-label">CARTES · {{ cardResults.length }} résultat{{ cardResults.length > 1 ? 's' : '' }}</div>
            <button
              v-for="(card, i) in cardResults"
              :key="'c-' + card.id"
              class="sp-result"
              :class="{ active: activeIndex === getFlatIndex('card', i) }"
              @click="select({ type: 'card', data: card })"
              @mousemove="activeIndex = getFlatIndex('card', i)"
            >
              <span class="sp-result-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="14" height="14" rx="2"/><path d="M21 9v10a2 2 0 0 1-2 2H9"/></svg>
              </span>
              <span class="sp-result-body">
                <span class="sp-result-title" v-html="highlight(truncate(card.recto, 70))"></span>
                <span class="sp-result-sub" v-html="highlight(truncate(card.verso, 60))"></span>
              </span>
              <kbd v-if="activeIndex === getFlatIndex('card', i)" class="sp-result-kbd">⏎</kbd>
            </button>
          </div>

          <!-- DOSSIERS -->
          <div v-if="folderResults.length > 0" class="sp-group">
            <div class="sp-group-label">DOSSIERS · {{ folderResults.length }} résultat{{ folderResults.length > 1 ? 's' : '' }}</div>
            <button
              v-for="(folder, i) in folderResults"
              :key="'f-' + folder.id"
              class="sp-result"
              :class="{ active: activeIndex === getFlatIndex('folder', i) }"
              @click="select({ type: 'folder', data: folder })"
              @mousemove="activeIndex = getFlatIndex('folder', i)"
            >
              <span class="sp-result-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              </span>
              <span class="sp-result-body">
                <span class="sp-result-title" v-html="highlight(truncate(folder.name, 70))"></span>
                <span class="sp-result-sub">{{ folder.nb_cards || '' }}{{ folder.nb_cards ? ' cartes' : 'Dossier' }}</span>
              </span>
            </button>
          </div>

          <!-- BIBLIOTHÈQUE PUBLIQUE -->
          <div v-if="libraryResults.length > 0" class="sp-group">
            <div class="sp-group-label">BIBLIOTHÈQUE PUBLIQUE · {{ libraryResults.length }} résultat{{ libraryResults.length > 1 ? 's' : '' }}</div>
            <button
              v-for="(pack, i) in libraryResults"
              :key="'l-' + (pack.id || i)"
              class="sp-result"
              :class="{ active: activeIndex === getFlatIndex('library', i) }"
              @click="select({ type: 'library', data: pack })"
              @mousemove="activeIndex = getFlatIndex('library', i)"
            >
              <span class="sp-result-icon sp-result-icon-lib">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/></svg>
              </span>
              <span class="sp-result-body">
                <span class="sp-result-title" v-html="highlight(truncate(pack.name || pack.title || '', 70))"></span>
                <span class="sp-result-sub">{{ pack.nb_cards ? pack.nb_cards + ' cartes · ' : '' }}Bibliothèque publique</span>
              </span>
            </button>
          </div>
        </div>

        <div v-else-if="showEmpty" class="sp-empty">
          Aucun résultat pour <em>{{ query }}</em>
        </div>

        <!-- Footer -->
        <div class="sp-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> Naviguer</span>
          <span><kbd>↵</kbd> Ouvrir</span>
          <span><kbd>Esc</kbd> Fermer</span>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { ref, watch, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { searchCardsByKeyword } from '@/services/cardApi'
import { fetchUserFolders } from '@/services/folderApi'
import { fetchExplorePage } from '@/services/exploreApi'
import { getSessionState } from '@/stores/sessionStore'

export default {
  name: 'SearchPalette',
  setup () {
    const router = useRouter()
    const sessionState = getSessionState()

    const visible = ref(false)
    const query = ref('')
    const cardResults = ref([])
    const folderResults = ref([])
    const libraryResults = ref([])
    const allFolders = ref([])
    const activeIndex = ref(0)
    const loading = ref(false)
    const inputEl = ref(null)
    const resultsEl = ref(null)

    const flatResults = computed(() => [
      ...cardResults.value.map(c => ({ type: 'card', data: c })),
      ...folderResults.value.map(f => ({ type: 'folder', data: f })),
      ...libraryResults.value.map(l => ({ type: 'library', data: l })),
    ])

    const hasResults = computed(() =>
      cardResults.value.length > 0 || folderResults.value.length > 0 || libraryResults.value.length > 0
    )

    const showEmpty = computed(() =>
      query.value.length >= 2 && !loading.value && !hasResults.value
    )

    let debounceTimer = null
    let debounceLibTimer = null

    async function show () {
      visible.value = true
      query.value = ''
      cardResults.value = []
      folderResults.value = []
      libraryResults.value = []
      activeIndex.value = 0
      if (allFolders.value.length === 0 && sessionState.userId) {
        try {
          const folders = await fetchUserFolders(sessionState.userId)
          allFolders.value = folders || []
        } catch { allFolders.value = [] }
      }
      nextTick(() => inputEl.value?.focus())
    }

    function close () {
      visible.value = false
      query.value = ''
      cardResults.value = []
      folderResults.value = []
      libraryResults.value = []
      activeIndex.value = 0
    }

    watch(query, (q) => {
      clearTimeout(debounceTimer)
      clearTimeout(debounceLibTimer)
      activeIndex.value = 0
      if (q.length < 2) {
        cardResults.value = []
        folderResults.value = []
        libraryResults.value = []
        loading.value = false
        return
      }
      loading.value = true
      // Client-side folder filter (instant)
      const lq = q.toLowerCase()
      folderResults.value = allFolders.value
        .filter(f => f.name?.toLowerCase().includes(lq))
        .slice(0, 3)
      // Cards: debounced API
      debounceTimer = setTimeout(async () => {
        try {
          const all = await searchCardsByKeyword(q, sessionState.userId)
          cardResults.value = (all || []).slice(0, 5)
        } catch { cardResults.value = [] } finally { loading.value = false }
      }, 250)
      // Library: debounced API, slower
      debounceLibTimer = setTimeout(async () => {
        try {
          const res = await fetchExplorePage({ search: q, page: 1 })
          libraryResults.value = ((res && res.results) || []).slice(0, 3)
        } catch { libraryResults.value = [] }
      }, 400)
    })

    function select (item) {
      if (!item) return
      close()
      if (item.type === 'card') router.push({ name: 'Card', params: { id: item.data.folder } })
      else if (item.type === 'folder') router.push({ name: 'Card', params: { id: item.data.id } })
      else if (item.type === 'library') router.push({ name: 'Explorer' })
    }

    function handleKeydown (e) {
      const total = flatResults.value.length
      if (e.key === 'Escape') { close(); return }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        activeIndex.value = Math.min(activeIndex.value + 1, total - 1)
        scrollActiveIntoView()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        activeIndex.value = Math.max(activeIndex.value - 1, 0)
        scrollActiveIntoView()
      } else if (e.key === 'Enter') {
        select(flatResults.value[activeIndex.value])
      }
    }

    function scrollActiveIntoView () {
      nextTick(() => {
        const el = resultsEl.value?.querySelector('.sp-result.active')
        el?.scrollIntoView({ block: 'nearest' })
      })
    }

    function getFlatIndex (type, localIndex) {
      if (type === 'card') return localIndex
      if (type === 'folder') return cardResults.value.length + localIndex
      return cardResults.value.length + folderResults.value.length + localIndex
    }

    function highlight (text) {
      if (!query.value || !text) return escapeHtml(text || '')
      const escapedQuery = query.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return escapeHtml(text).replace(
        new RegExp(escapedQuery, 'gi'),
        m => `<mark class="sp-mark">${m}</mark>`
      )
    }

    function escapeHtml (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    }

    function truncate (text, max) {
      if (!text) return ''
      return text.length > max ? text.slice(0, max) + '…' : text
    }

    return {
      visible, query, cardResults, folderResults, libraryResults, flatResults,
      activeIndex, loading, inputEl, resultsEl, hasResults, showEmpty,
      show, close, handleKeydown, highlight, select, truncate, getFlatIndex
    }
  }
}
</script>

<style scoped>
.sp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 27, 50, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  z-index: 9999;
}

.sp-palette {
  width: 100%;
  max-width: 600px;
  background: var(--color-surface);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}

/* Input */
.sp-input-wrap {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: 0 var(--s-4);
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.sp-search-icon {
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.sp-input {
  flex: 1;
  height: 52px;
  border: none;
  background: transparent;
  font-size: 16px;
  color: var(--color-text);
  font-family: var(--font-sans);
  outline: none;
}

.sp-input::placeholder {
  color: var(--color-text-muted);
}

.sp-esc-hint {
  font-size: 10px;
  padding: 3px 7px;
  border-radius: 4px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  font-family: ui-monospace, Menlo, monospace;
  color: var(--color-text-muted);
  font-weight: 500;
  flex-shrink: 0;
}

/* Results */
.sp-results {
  overflow-y: auto;
  flex: 1;
}

.sp-group {
  padding: 6px 8px 10px;
}

.sp-group-label {
  padding: 8px 8px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  color: var(--color-text-muted);
  text-transform: uppercase;
}

.sp-result {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 8px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: var(--font-sans);
  border-radius: var(--r-sm);
  transition: background var(--transition-fast);
}

.sp-result.active {
  background: var(--color-primary-soft);
}

.sp-result:hover {
  background: var(--color-surface-2);
}

.sp-result.active:hover {
  background: var(--color-primary-soft);
}

.sp-result-icon {
  width: 28px;
  height: 28px;
  border-radius: var(--r-sm);
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sp-result-icon-lib {
  background: oklch(96% 0.04 250);
  color: var(--color-primary);
}

[data-theme="dark"] .sp-result-icon-lib {
  background: oklch(25% 0.04 250);
  color: oklch(70% 0.08 250);
}

.sp-result-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sp-result-title {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sp-result-sub {
  display: block;
  font-size: 12px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sp-result-kbd {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  font-family: ui-monospace, Menlo, monospace;
  color: var(--color-text-muted);
  font-weight: 500;
  flex-shrink: 0;
}

/* Highlight mark */
.sp-results :deep(.sp-mark) {
  background: oklch(94% 0.10 100);
  color: oklch(40% 0.10 70);
  border-radius: 2px;
  padding: 0 1px;
}

/* Empty state */
.sp-empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 14px;
  color: var(--color-text-muted);
}

.sp-empty em {
  font-style: normal;
  font-weight: 500;
  color: var(--color-text);
}

/* Footer */
.sp-footer {
  display: flex;
  gap: var(--s-5);
  align-items: center;
  padding: 8px 16px;
  background: var(--color-surface-2);
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.sp-footer span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--color-text-muted);
}

kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--r-sm);
  font-size: 10px;
  font-family: inherit;
}

@media (max-width: 639px) {
  .sp-overlay {
    padding-top: 20px;
    align-items: flex-start;
  }

  .sp-palette {
    margin: 0 var(--s-3);
    max-height: calc(100vh - 40px);
  }

  .sp-footer {
    gap: var(--s-4);
    flex-wrap: wrap;
  }
}
</style>
